import { ElMessage, ElMessageBox } from "element-plus";
import type { MessageType } from "element-plus";
import { h } from "vue";
import i18n from "@/locales";

/**
 * 消息提示方法
 * @param message - 提示消息内容
 * @param type - 消息类型，默认为 'success'（可选值：'success', 'warning', 'info', 'error'）
 * @param dangerouslyUseHTMLString - 是否将消息作为 HTML 渲染，默认为 true
 * @param centerTop - 是否顶部偏移--默认false
 * @param duration - 显示时长（毫秒），默认 2000
 */
export function toast(
  message: string,
  type: MessageType = "success",
  dangerouslyUseHTMLString: boolean = true,
  centerTop: boolean = false,
  duration: number = 2000,
): void {
  const renderedMessage = dangerouslyUseHTMLString
    ? `<div style="text-align: center;">${message}</div>`
    : message;

  ElMessage({
    message: renderedMessage,
    type: type,
    dangerouslyUseHTMLString: dangerouslyUseHTMLString,
    duration: duration,
    offset: centerTop ? 10 : 80,
  });
}

export function showModal(
  content: string = "提示内容",
  type: MessageType = "warning",
  title: string = "",
): Promise<"confirm" | "cancel" | "close"> {
  return ElMessageBox.confirm(content, title, {
    confirmButtonText: i18n.global.t("button.confirm"),
    cancelButtonText: i18n.global.t("button.cancel"),
    type,
  });
}

/**
 * 重要提示统一 alert 确认框：必须点确认关闭，避免 toast 一闪而过被忽略；
 * 确认按钮文案跟随当前语言，个别差异（如 showClose）通过第三个参数覆盖。
 * @param message - 提示内容
 * @param type - 图标类型，默认 'warning'
 * @param options - 附加 ElMessageBox 配置，可覆盖默认行为
 */
export async function showValidationAlert(
  message: string,
  type: MessageType = "warning",
  options: Record<string, unknown> = {},
): Promise<void> {
  await ElMessageBox.alert(message, "", {
    type,
    confirmButtonText: i18n.global.t("button.confirm"),
    showClose: false,
    closeOnClickModal: false,
    closeOnPressEscape: false,
    ...options,
  }).catch(() => undefined);
}

export interface ShowOrderConfirmOptions {
  title?: string;
  orderLabel?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  type?: MessageType;
}

/**
 * 带订单编号的统一确认框。
 * 调用方传入已翻译的文案，方便在不同语言页面中复用。
 */
export function showOrderConfirm(
  message: string,
  orderCode: string | number | null | undefined,
  options: ShowOrderConfirmOptions = {},
) {
  const {
    title = i18n.global.t("confirm.alert_title"),
    orderLabel = i18n.global.t("frmSearch.sOrderNo"),
    confirmButtonText = i18n.global.t("button.confirm"),
    cancelButtonText = i18n.global.t("button.cancel"),
    type = "warning",
  } = options;

  return ElMessageBox.confirm(
    h("div", { class: "common-order-confirm-content" }, [
      h("p", { class: "common-order-confirm-question" }, message),
      h("div", { class: "common-order-confirm-code" }, [
        h("span", null, `${orderLabel}：`),
        h("strong", null, String(orderCode || "-")),
      ]),
    ]),
    title,
    {
      type,
      customClass: "common-order-confirm",
      confirmButtonText,
      cancelButtonText,
    },
  );
}

interface ShowCustomValueOptions {
  codeKey?: string;
  nameKey?: string;
  defaultValue?: string;
}

export const showCustomValueByCode = (
  list: Array<Record<string, unknown>>,
  code: unknown,
  {
    codeKey = "code",
    nameKey = "name",
    defaultValue = "-",
  }: ShowCustomValueOptions = {},
): string => {
  // 参数检查
  if (!Array.isArray(list)) {
    console.warn("Expected an array, but got:", list);
    return defaultValue;
  }
  if (code === null || code === undefined) return defaultValue;

  // 查找匹配项
  const item = list.find((i) => i[codeKey] == code);
  if (!item) return defaultValue;

  const name = item[nameKey] as string | undefined;
  if (!name) return defaultValue;

  // 提取冒号后的文本
  const parts = name.split(":");
  const text = parts[parts.length - 1].trim();

  return text || defaultValue;
};
